import { isEqual } from 'lodash'
import { z } from 'zod'
import { LocalStorage } from '@library/enums/local-storage'
import { joinText } from '@library/utils/functions'
import { isType } from '@library/utils/functions/is-type'
import { useEnv } from './use-env'

export function useStorage() {
  const { VITE_STORAGE_PREFIX_KEY } = useEnv()

  const getStorageData = <T extends z.ZodType>(
    option:
      | LocalStorage
      | {
          key: LocalStorage
          zodSchema: T
          autoRemove?: boolean
        }
  ): z.infer<T> | undefined => {
    const key = typeof option === 'string' ? option : option.key
    const zodSchema = typeof option === 'string' ? z.any() : option.zodSchema
    const autoRemove =
      typeof option === 'string' ? false : (option.autoRemove ?? false)

    const storageData = localStorage.getItem(getStorageKey(key))

    if (autoRemove) {
      removeStorageData(key)
    }

    if (!storageData) {
      return undefined
    }

    const data = parseStorageData(storageData)

    if (isType(data, zodSchema, true)) {
      return data
    } else {
      removeStorageData(key)
      return undefined
    }
  }

  const parseStorageData = (value: string): unknown => {
    try {
      const parseData = JSON.parse(value)

      return typeof parseData === 'object' ? parseData : value
    } catch {
      return value
    }
  }

  const setStorageData = (key: LocalStorage, value: any) => {
    const storageData = getStorageData(key)

    if (isEqual(storageData, value)) {
      return
    }

    localStorage.setItem(
      getStorageKey(key),
      typeof value === 'object' ? JSON.stringify(value) : value
    )
  }

  const removeStorageData = (key: LocalStorage) => {
    localStorage.removeItem(getStorageKey(key))
  }

  const getStorageKey = (key: string): string => {
    return `${joinText([VITE_STORAGE_PREFIX_KEY, key], '__')}__`
  }

  return {
    getStorageData,
    setStorageData,
    removeStorageData,
    getStorageKey
  }
}
